import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $initialCurrenciesAvailableStore, $yearlyTransactionStore } from '@lib/store';
import { cn, DateUtils, PaletteUtils } from '@shared/utils';
import { DurationType, type PaymentSummariesResponse, TransactionDirection } from '@shared/api';
import { TransactionItem } from './transactionItem';
// import { RequestTransaction } from './requestTransaction';
import { View } from '@widgets';
import { Select, Tabs, Text } from '@shared/widgets';

interface YearlyTransactionProps {
  minimalYear: number;
}

export const YearlyTransaction = (props: YearlyTransactionProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const payment = useUnit($yearlyTransactionStore.store);

  const yearsList = useMemo(() => DateUtils.getYearsList(props.minimalYear), [props.minimalYear]);
  const [selectedYear, setSelectedYear] = useState<string>(yearsList[yearsList.length - 1]);
  const [currency, setCurrency] = useState<string>(currenciesAvailableStore.data[0]);
  const [tab, setTab] = useState<number>(1);

  const { t } = useTranslation('dashboard');

  useEffect(() => {
    if (!currency) return;

    $yearlyTransactionStore.events.update({
      Currency: currency!,
      DateFrom: DateUtils.getFirstDateOfYear(selectedYear, '01'),
      DateTo: DateUtils.getLastDateOfYear(selectedYear, '12'),
    });

    return () => $yearlyTransactionStore.events.clear();
  }, [currency, selectedYear]);

  const tabs = useMemo(
    () => [
      { label: t('payment.tabs.outgoingTransactions'), value: 1 },
      { label: t('payment.tabs.incomingTransactions'), value: 2 },
      // { label: t('payment.tabs.requestOutgoingSummary'), value: 3 },
    ],
    [t],
  );
  const content = useMemo(() => {
    switch (tab) {
      case 1:
        return (
          <TransactionItem
            {...(payment.data as PaymentSummariesResponse).outgoingTransactions}
            textValue={selectedYear}
            currency={currency}
            itemType={TransactionDirection.Outgoing}
            type={DurationType.Yearly}
          />
        );
      case 2:
        return (
          <TransactionItem
            {...(payment.data as PaymentSummariesResponse).incomingTransactions}
            textValue={selectedYear}
            currency={currency}
            itemType={TransactionDirection.Incoming}
            type={DurationType.Yearly}
          />
        );
      // case 3:
      //   return (
      //     <RequestTransaction
      //       {...(payment.data as PaymentSummariesResponse).outgoingTransactionSummary}
      //       textValue={selectedYear!}
      //       currency={currency!}
      //     />
      //   );
      default:
        return null;
    }
  }, [tab, currency, selectedYear, payment.loading, payment.data]);
  return (
    <div
      className={cn(
        PaletteUtils.isInsigniaPalette && 'flex flex-col gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
      )}
    >
      <div className={cn('flex items-center justify-between', PaletteUtils.isVcfcPalette && ['p-6 pb-0', 'max-md:flex-col max-md:items-start'])}>
        <Text className='mr-16' weight={PaletteUtils.isVcfcPalette ? 'semibold' : undefined} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
          {t('payment.yearlyTransaction.title')}
        </Text>
        <div className={cn('flex gap-6', PaletteUtils.isVcfcPalette && 'max-md:mt-3 max-md:w-full max-md:flex-col')}>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={(value) => setCurrency(value!)} />
          <Select items={yearsList} value={selectedYear} onChange={(value) => setSelectedYear(value!)} />
        </div>
      </div>
      <View
        loadingClassName={cn(PaletteUtils.isVcfcPalette && 'pb-6 pt-4')}
        errorClassName={cn(PaletteUtils.isVcfcPalette && 'p-6 pt-4')}
        loading={payment.loading}
        error={payment.error}
        onRefresh={() =>
          $yearlyTransactionStore.events.update({
            Currency: currency!,
            DateFrom: DateUtils.getFirstDateOfYear(selectedYear, '01'),
            DateTo: DateUtils.getLastDateOfYear(selectedYear, '12'),
          })
        }
      >
        <div className={cn('relative flex', PaletteUtils.isVcfcPalette && ['pl-2', 'max-md:no-scrollbar max-md:overflow-x-auto max-md:pb-4'])}>
          <Tabs className='w-full p-4' options={tabs} value={tab} onChange={setTab} />
        </div>
        {content}
      </View>
    </div>
  );
};
